// Sorting a list table by one of its columns: a click on a column title orders the rows by it,
// a second click turns the order round, a third gives the list its own order back.

/** What a column orders its rows by; an empty value (null, undefined, '') always goes last. */
export type SortValue = string | number | boolean | Date | null | undefined;
export type SortDir = 'asc' | 'desc';

export interface SortState {
  readonly key: string;
  readonly dir: SortDir;
}

export interface SortColumn<T> {
  /** the column's title, also the phone's "Sırala" choice */
  readonly label: string;
  readonly value: (row: T) => SortValue;
  /** a first click puts the largest / newest first (quantities, dates of the past) */
  readonly desc?: boolean;
}

export type SortColumns<T> = Readonly<Record<string, SortColumn<T>>>;

// Turkish letters in their places, "A-10" after "A-9"
const collator = new Intl.Collator('tr', { numeric: true, sensitivity: 'base' });

const empty = (v: SortValue): v is null | undefined | '' => v === null || v === undefined || v === '';

const plain = (v: Exclude<SortValue, null | undefined>): string | number => (v instanceof Date ? v.getTime() : typeof v === 'boolean' ? Number(v) : v);

/** Ascending order of two values: numbers as numbers, texts as a Turkish reader sorts them. */
export function compareValues(a: SortValue, b: SortValue): number {
  if (empty(a) || empty(b)) return empty(a) ? (empty(b) ? 0 : 1) : -1;
  const x = plain(a);
  const y = plain(b);
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return collator.compare(String(x), String(y));
}

/** The rows in the column's order; equal rows keep the list's own order, empty values stay last either way. */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  return rows
    .map((row, i) => ({ row, i, v: value(row) }))
    .sort((a, b) => {
      if (empty(a.v) || empty(b.v)) return compareValues(a.v, b.v) || a.i - b.i;
      return (dir === 'asc' ? compareValues(a.v, b.v) : compareValues(b.v, a.v)) || a.i - b.i;
    })
    .map((e) => e.row);
}

/** What a click on a column title does: its first order, then the other way round, then the list's own order. */
export function nextSort(current: SortState | null, key: string, firstDesc = false): SortState | null {
  const first: SortDir = firstDesc ? 'desc' : 'asc';
  if (current?.key !== key) return { key, dir: first };
  return current.dir === first ? { key, dir: first === 'asc' ? 'desc' : 'asc' } : null;
}

/** A saved order read back: only one of the table's columns counts. */
export function parseSort(saved: string | null, keys: readonly string[]): SortState | null {
  if (!saved) return null;
  const [key, dir] = saved.split(':');
  return key && keys.includes(key) && (dir === 'asc' || dir === 'desc') ? { key, dir } : null;
}
